import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { OperationsApi, OperationsDto } from '@buspro/web/apis';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NGXLogger } from 'ngx-logger';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-my-audits-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzButtonModule,
    NzIconModule,
    NzCardModule,
    NzModalModule,
    NzSpinModule,
    NzStatisticModule,
    MomentModule,
  ],
  templateUrl: './my-audits-page.component.html',
  styleUrls: ['./my-audits-page.component.scss'],
})
export class MyAuditsPageComponent {
  private operationsApi = inject(OperationsApi);
  private router = inject(Router);
  private modal = inject(NzModalService);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private queryClient = injectQueryClient();

  // Every audit this user holds open, oldest first.
  //
  // Was a single `my-current-audit`, which was correct while one was the maximum. Local
  // Administrators and Local Managers may now hold several, and a second open audit that
  // no screen names is one that goes missing — the failure mode a single stalled audit
  // already demonstrated by disabling the whole Ready for Audit page.
  auditsInProgressQuery = injectQuery(() => ({
    queryKey: ['my-audits-in-progress'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditsInProgress()),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  }));

  // Query for audit history
  auditHistoryQuery = injectQuery(() => ({
    queryKey: ['my-audit-history'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditHistory()),
    refetchInterval: 60000, // Auto-refresh every minute
  }));

  // Query for audit stats
  auditStatsQuery = injectQuery(() => ({
    queryKey: ['my-audit-stats'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditStats()),
    refetchInterval: 60000, // Auto-refresh every minute
  }));

  // Mutation for cancelling audit
  cancelAuditMutation = injectMutation(() => ({
    mutationFn: (operationId: string) =>
      lastValueFrom(this.operationsApi.operationsControllerCancelAudit({ id: operationId })),
    onSuccess: () => {
      this.notification.success('✅ Audit Cancelled', 'The audit has been cancelled and returned to the queue.');
      // Invalidate all audit-related queries to refresh badges and lists
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-current-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-ready-for-audit-badge'] });
      this.auditsInProgressQuery.refetch();
      this.auditHistoryQuery.refetch();
      this.auditStatsQuery.refetch();
      // Only leave the page when nothing is left open here. With several audits in flight,
      // releasing one and being bounced away from the rest is disorienting.
      if (this.auditsInProgress().length <= 1) {
        setTimeout(() => {
          this.router.navigate(['/audits/ready']);
        }, 500);
      }
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to cancel audit:', error);
      this.notification.error('❌ Failed to Cancel Audit', 'An error occurred while cancelling the audit.');
    },
  }));

  /** Audits this user currently holds open, oldest first. */
  auditsInProgress(): OperationsDto[] {
    return this.auditsInProgressQuery.data() ?? [];
  }

  hasAuditInProgress(): boolean {
    return this.auditsInProgress().length > 0;
  }

  /**
   * Navigate to audit detail screen
   */
  resumeAudit(operationId: string) {
    this.router.navigate(['/audits', operationId, 'detail']);
  }

  /**
   * Cancel audit with confirmation
   */
  cancelAudit(operation: OperationsDto) {
    this.modal.confirm({
      nzTitle: 'Cancel Audit?',
      nzContent: `Are you sure you want to cancel the audit for ${operation.auditNumber}? The operation will be returned to the queue for others to audit.`,
      nzOkText: 'Yes, Cancel Audit',
      nzOkDanger: true,
      nzCancelText: 'No, Keep Auditing',
      nzOnOk: () => {
        this.cancelAuditMutation.mutate(operation.id);
      },
    });
  }

  /**
   * View the comprehensive per-trip audit loss report.
   */
  viewReport(operation: OperationsDto) {
    this.router.navigate(['/audits', operation.id, 'report']);
  }

  /**
   * Format duration from seconds to human-readable string
   */
  formatDuration(seconds: number | null | undefined): string {
    if (!seconds) {
      return 'N/A';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
  }

  /**
   * Refresh all queries
   */
  refresh() {
    this.auditsInProgressQuery.refetch();
    this.auditHistoryQuery.refetch();
    this.auditStatsQuery.refetch();
  }
}
